import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../../lib/api';
import { Enablement } from '../../../types';

interface UseEnablementParams {
  projectId: string;
}

const API_URL = getApiBaseUrl();

// Get auth token from Supabase session
const getAuthToken = async () => {
  const { supabase } = await import('../../../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

export const useEnablement = ({ projectId }: UseEnablementParams) => {
  const [enablement, setEnablement] = useState<Enablement | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Load enablement data
  useEffect(() => {
    const loadEnablement = async () => {
      setLoading(true);
      try {
        const token = await getAuthToken();
        if (!token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_URL}/api/enablement/${projectId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            // No enablement found - this is fine
            setEnablement(null);
            return;
          }
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch enablement');
        }

        const data = await response.json();
        if (data.enablement) {
          const enablementData = {
            ...data.enablement,
            createdAt: new Date(data.enablement.created_at),
            updatedAt: new Date(data.enablement.updated_at)
          };
          setEnablement(enablementData);
          setLastSaved(new Date(data.enablement.updated_at));
        } else {
          setEnablement(null);
        }
      } catch (error) {
        console.error('Error loading enablement:', error);
        setEnablement(null);
      } finally {
        setLoading(false);
      }
    };

    loadEnablement();
  }, [projectId]);

  // Save enablement
  const saveEnablement = useCallback(async (data: Partial<Enablement>) => {
    setSaving(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Determine what instructions to save
      const instructionsToSave = data.instructions || enablement?.instructions || { content: '', sections: [] };
      
      console.log('Saving enablement with instructions:', instructionsToSave);
      
      const response = await fetch(`${API_URL}/api/enablement/${projectId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instructions: instructionsToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save enablement');
      }

      const responseData = await response.json();
      console.log('Save response:', responseData);
      
      const updatedEnablement = {
        ...responseData.enablement,
        createdAt: new Date(responseData.enablement.created_at),
        updatedAt: new Date(responseData.enablement.updated_at)
      };

      setEnablement(updatedEnablement);
      setLastSaved(new Date());
      
      console.log('Enablement saved successfully');
      return updatedEnablement;
    } catch (error) {
      console.error('Error saving enablement:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [projectId, enablement]);

  // Auto-save draft
  const autoSave = useCallback(async (content: string) => {
    // Always allow auto-save when content changes
    await saveEnablement({
      instructions: {
        ...enablement?.instructions,
        content
      }
    });
  }, [enablement, saveEnablement]);

  // Publish enablement
  const publishEnablement = useCallback(async () => {
    try {
      console.log('publishEnablement called for projectId:', projectId);
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      console.log('Making request to:', `${API_URL}/api/enablement/${projectId}/publish`);
      const response = await fetch(`${API_URL}/api/enablement/${projectId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to publish enablement');
      }

      const updatedEnablement = {
        ...responseData.enablement,
        createdAt: new Date(responseData.enablement.created_at),
        updatedAt: new Date(responseData.enablement.updated_at)
      };

      setEnablement(updatedEnablement);
      console.log('Enablement updated successfully');
      return updatedEnablement;
    } catch (error) {
      console.error('Error publishing enablement:', error);
      throw error;
    }
  }, [projectId]);

  // Create new enablement
  const createEnablement = useCallback(async (initialContent: string) => {
    return await saveEnablement({ 
      instructions: {
        content: initialContent,
        sections: []
      }
    });
  }, [saveEnablement]);

  return {
    enablement,
    loading,
    saving,
    lastSaved,
    saveEnablement,
    autoSave,
    publishEnablement,
    createEnablement
  };
};